import React, { useState } from 'react';

/**
 * An admin delete its names HELD (ROADMAP 63; Milos, 2026-10-17: "Show held deletions in the admin UI").
 *
 * When a public name on an account's addresses cannot be removed, the delete stops before anything irreversible: no
 * address is released, nothing changes in billing, and the account, its records and its login stay (an abuse delete
 * also powers its machines off). So the account is still in every list and still billing, and before this the console
 * only ever said "Teardown started". GET /v1/admin/delete-holds answers these; the admin page shows this badge on the
 * account's row in both account tables, and the badge opens the names that held it.
 */
export interface DeleteHoldName { id: string; domains: string[]; target: string; why: string[] }

export interface DeleteHold {
    user_id: string;
    username?: string | null;
    names_held: boolean;
    abuse: boolean;
    workflow_id: string | null;
    held_since: string;
    held_at: string;
    names: DeleteHoldName[];
    failures: string[];
    powered_off: {
        stopped?: string[]; failed?: string[]; skipped?: string[]; reason?: string | null;
        account_suspended?: boolean; suspend_error?: string | null; unrecorded?: string[];
    } | null;
    /** The account's admin suspension as of now (null: no billing row). A held abuse delete sets it; Unsuspend lifts it. */
    suspended_by_admin?: boolean | null;
}

const WHAT_HELD = "The delete stopped before anything irreversible: a public name on this account's addresses could not be removed. No address was released, nothing changed in billing, and the account, its records and its login are kept, so it keeps billing while its machines exist.";
const NEXT = 'Settle these names (or the reason in the report), then run Delete account again.';
const NO_NAMES = 'None listed: the names step itself failed, and its reason is in the report below.';

/**
 * What powering the machines off did, for an abuse delete; null for a plain delete, which leaves them running. And whether
 * the account can start them again: a held abuse delete suspends it (F2 and SEC-3 of the 2026-10-17 review), and that is
 * read live, so the line stays true after an admin's Unsuspend, or when the suspension could not be written.
 */
export function powerOffLine(h: DeleteHold): string | null {
    if (!h.abuse) return null;
    const p = h.powered_off;
    const parts = p ? [`${p.stopped?.length ?? 0} machine(s) powered off`] : ['its machines were not powered off (the report below says why)'];
    if (p?.failed?.length) parts.push(`${p.failed.length} could not be stopped (${p.failed.join(', ')})`);
    if (p?.reason) parts.push(`not stopped: ${p.reason}`);
    if (h.suspended_by_admin === true) parts.push('the account is suspended: it cannot start, reboot or create machines (Unsuspend lifts it)');
    if (h.suspended_by_admin === false) parts.push('the account is NOT suspended: it can start its machines again');
    return `Abuse delete: ${parts.join('; ')}.`;
}

/** One name that held the delete, as the panel lists it. */
export function heldNameLine(n: DeleteHoldName): string {
    return `#${n.id} ${n.domains.join(', ') || '(no domain)'} on ${n.target || '(no address)'}: ${n.why.join('; ')}`;
}

const DeleteHeldBadge: React.FC<{ hold: DeleteHold }> = ({ hold }) => {
    const [open, setOpen] = useState(false);
    const since = new Date(hold.held_since).toLocaleString();
    const lastRun = new Date(hold.held_at).toLocaleString();
    const names = hold.names ?? [];
    const failures = hold.failures ?? [];
    const powerOff = powerOffLine(hold);
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title={`Delete held since ${since}: ${names.length} name(s) held it. Click for the names.`}
                style={{
                    marginLeft: 6, background: '#ef444422', color: '#ef4444', border: 'none', borderRadius: 999,
                    padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                }}
            >
                delete held
            </button>
            {open && (
                <div
                    onClick={() => setOpen(false)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <div
                        role="dialog"
                        aria-label="Held delete"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)',
                            borderRadius: 10, padding: 20, width: 'calc(100% - 32px)', maxWidth: 680, maxHeight: '80vh',
                            overflowY: 'auto', whiteSpace: 'normal', boxShadow: '0 8px 24px rgba(0,0,0,0.28)', fontSize: '0.85rem',
                        }}
                    >
                        <h3 style={{ margin: '0 0 8px', fontSize: '1rem' }}>{`Delete held: ${hold.username || hold.user_id}`}</h3>
                        <p style={{ margin: '0 0 8px' }}>{WHAT_HELD}</p>
                        {powerOff && <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{powerOff}</p>}
                        <h4 style={{ margin: '12px 0 4px', fontSize: '0.85rem' }}>Names that held it</h4>
                        {names.length ? (
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {names.map((n) => <li key={n.id} style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{heldNameLine(n)}</li>)}
                            </ul>
                        ) : (
                            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{NO_NAMES}</p>
                        )}
                        {failures.length > 0 && (
                            <>
                                <h4 style={{ margin: '12px 0 4px', fontSize: '0.85rem' }}>Report</h4>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {failures.map((f, i) => <li key={i} style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{f}</li>)}
                                </ul>
                            </>
                        )}
                        <p style={{ margin: '12px 0 4px' }}>{NEXT}</p>
                        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                            {`Held since ${since}; last run ${lastRun}; workflow ${hold.workflow_id ?? 'unknown'}.`}
                        </p>
                        <div style={{ textAlign: 'right', marginTop: 12 }}>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-primary)' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DeleteHeldBadge;
